import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  FileSpreadsheet,
  LoaderCircle,
  Play,
  RotateCcw,
  Upload,
} from "lucide-react";
import { api } from "../api";

export function RunPanel() {
  const queryClient = useQueryClient();
  const configQuery = useQuery({
    queryKey: ["config"],
    queryFn: api.getConfig,
  });
  const [inputPath, setInputPath] = useState("data/products.sample.csv");
  const [limit, setLimit] = useState("");
  const [message, setMessage] = useState("使用样例表，可直接运行完整批次。");
  const llmConfig = configQuery.data?.adapters.llm;
  const videoConfig = configQuery.data?.adapters.video;
  const isLiveLlm = Boolean(llmConfig?.live_calls_enabled);
  const isLiveVideo = Boolean(videoConfig?.live_calls_enabled);
  const isPublicPaid = configQuery.data?.mode === "public_paid";
  const hasLiveCalls = !isPublicPaid && (isLiveLlm || isLiveVideo);
  const isPublicDemo = configQuery.data?.mode === "public_demo";
  const isPublicWorkspace = isPublicDemo || isPublicPaid;
  const canImport = configQuery.data?.capabilities.importCsv ?? true;

  const importMutation = useMutation({
    mutationFn: ({ filename, content }: { filename: string; content: string }) =>
      api.importCsv(filename, content),
    onSuccess: (data) => {
      setInputPath(data.input_path);
      setMessage(`已导入 ${data.product_count} 个有效商品，发现 ${data.issues.length} 个提示。`);
    },
    onError: (error: Error) => setMessage(error.message),
  });

  const validateMutation = useMutation({
    mutationFn: () => api.validateCsv(inputPath),
    onSuccess: (data) =>
      setMessage(
        data.valid
          ? `校验通过：${data.product_count} 个商品，${data.issues.length} 个提示。`
          : `校验未通过：${data.issues.length} 个问题待处理。`,
      ),
    onError: (error: Error) => setMessage(error.message),
  });

  const runMutation = useMutation({
    mutationFn: () =>
      api.runWorkflow(inputPath, limit ? Number.parseInt(limit, 10) : null),
    onSuccess: async (data) => {
      setMessage(`已完成 ${data.product_count} 个商品、${data.script_count} 版脚本。`);
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      await queryClient.invalidateQueries({ queryKey: ["reviews"] });
    },
    onError: (error: Error) => setMessage(error.message),
  });

  const resetMutation = useMutation({
    mutationFn: api.resetDemo,
    onSuccess: async (data) => {
      setInputPath("data/products.sample.csv");
      setLimit("");
      setMessage(data.message);
      await queryClient.invalidateQueries();
    },
    onError: (error: Error) => setMessage(error.message),
  });

  const isBusy =
    importMutation.isPending ||
    validateMutation.isPending ||
    runMutation.isPending ||
    resetMutation.isPending;

  async function handleFile(file: File | undefined) {
    if (!file) return;
    const content = await file.text();
    importMutation.mutate({ filename: file.name, content });
  }

  return (
    <section className="run-panel" aria-labelledby="run-title">
      <div className="section-heading compact-heading">
        <div>
          <span className="section-index">Action 01</span>
          <h2 id="run-title">启动生产批次</h2>
        </div>
        <span className={`safe-chip${hasLiveCalls ? " is-live" : ""}`}>
          <CheckCircle2 size={13} aria-hidden="true" />
          {isPublicPaid
            ? "沙盒编排预演"
            : isLiveVideo
              ? "视频实时调用"
              : isLiveLlm
                ? "LLM 实时调用"
                : "Mock 安全"}
        </span>
      </div>

      {hasLiveCalls && (
        <p className="live-call-notice">
          当前
          {[
            isLiveLlm ? `LLM（${llmConfig?.provider}）` : "",
            isLiveVideo ? `视频（${videoConfig?.provider}）` : "",
          ]
            .filter(Boolean)
            .join("、")}
          将发起实时 API 调用并产生用量；其他节点按各自 Adapter 配置执行。
        </p>
      )}

      {isPublicDemo && (
        <p className="live-call-notice">
          当前是两小时临时沙盒：只使用内置样例和 Mock Adapter，刷新会保留本会话修改。
        </p>
      )}

      {isPublicPaid && (
        <p className="live-call-notice">
          此处只运行内置样例的沙盒编排，不触发供应商 API；真实生成请到商品的“生成路由”，通过访问码和费用二次确认后提交。
        </p>
      )}

      <label className="field-stack">
        <span>输入商品表</span>
        <div className="path-field">
          <FileSpreadsheet size={17} aria-hidden="true" />
          <input
            value={inputPath}
            onChange={(event) => setInputPath(event.target.value)}
            readOnly={isPublicWorkspace}
            spellCheck={false}
          />
        </div>
      </label>

      <div className="run-options">
        <label className="field-stack">
          <span>处理上限</span>
          <input
            className="number-input"
            value={limit}
            min="1"
            max={isPublicWorkspace ? "6" : "1000"}
            type="number"
            placeholder="全部"
            onChange={(event) => setLimit(event.target.value)}
          />
        </label>
        {canImport ? (
          <label className="upload-button">
            <Upload size={16} aria-hidden="true" />
            导入 CSV
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => void handleFile(event.target.files?.[0])}
            />
          </label>
        ) : (
          <button className="upload-button" type="button" disabled>
            <Upload size={16} aria-hidden="true" />
            公开 Demo 禁止上传
          </button>
        )}
      </div>

      <p className={message.includes("失败") || message.includes("不") ? "panel-message is-error" : "panel-message"}>
        {message}
      </p>

      <div className="button-row">
        {isPublicWorkspace && (
          <button
            type="button"
            className="secondary-button"
            onClick={() => resetMutation.mutate()}
            disabled={isBusy}
          >
            <RotateCcw size={16} aria-hidden="true" />
            重置演示
          </button>
        )}
        <button
          type="button"
          className="secondary-button"
          onClick={() => validateMutation.mutate()}
          disabled={isBusy}
        >
          校验数据
        </button>
        <button
          type="button"
          className="primary-button"
          onClick={() => runMutation.mutate()}
          disabled={isBusy}
        >
          {runMutation.isPending ? (
            <LoaderCircle className="is-spinning" size={17} aria-hidden="true" />
          ) : (
            <Play size={17} fill="currentColor" aria-hidden="true" />
          )}
          {runMutation.isPending
            ? "生产中…"
            : isPublicPaid
              ? "运行沙盒工作流"
              : "运行工作流"}
        </button>
      </div>
    </section>
  );
}
